// src/components/ui/Mobile/MobileBottomNav.tsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, MessageCircle, ShoppingCart, User, UserCircle, CreditCard } from 'lucide-react';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './MobileBottomNav.module.css';

interface MobileBottomNavProps {
  className?: string;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  className = ''
}) => {
  const { isMobile, isTablet, isLandscape } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Если не мобильное устройство, не отображаем меню
  if (!isMobile && !isTablet) return null;
  
  // Определяем активный раздел на основе текущего URL
  const isActive = (path: string, exact: boolean = false): boolean => {
    if (exact) {
      // Точное совпадение пути для страниц профиля и настроек
      return location.pathname === path;
    }
    // Для других страниц используем вхождение пути
    return location.pathname.includes(path);
  };
  
  // Определяем, нужно ли показывать компактные иконки
  const useCompactIcons = isLandscape || window.innerWidth < 375;
  
  // Обновленная структура меню с корректными путями
  const navItems = [
    { 
      id: 'chats', 
      label: 'Чаты', 
      icon: <MessageCircle size={useCompactIcons ? 20 : 24} />, 
      path: '/personal/platforms/whatsapp',
      exact: false
    },
    { 
      id: 'orders', 
      label: 'Заказы', 
      icon: <ShoppingCart size={useCompactIcons ? 20 : 24} />, 
      path: '/personal/platforms/orders',
      exact: false
    },
    { 
      id: 'clients', 
      label: 'Клиенты', 
      icon: <User size={useCompactIcons ? 20 : 24} />, 
      path: '/personal/platforms/clients',
      exact: false
    },
    { 
      id: 'dashboard', 
      label: 'Дашборд', 
      icon: <Home size={useCompactIcons ? 20 : 24} />, 
      path: '/personal/analytics/dashboard',
      exact: false
    },
    { 
      id: 'profile', 
      label: 'Профиль', 
      icon: <UserCircle size={useCompactIcons ? 20 : 24} />, 
      path: '/personal/profile',
      exact: true
    },
    { 
      id: 'subscriptions', 
      label: 'Подписки', 
      icon: <CreditCard size={useCompactIcons ? 20 : 24} />, 
      path: '/personal/user-settings',
      exact: true
    }
  ];
  
  // Функция для навигации
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  
  // Добавляем класс для ландшафтной ориентации
  const navContainerClass = isLandscape ? `${styles.navContainer} ${styles.navContainerLandscape}` : styles.navContainer;
  const navContentClass = window.innerWidth < 375 ? `${styles.navContent} ${styles.navContentSmall}` : styles.navContent;

  return (
    <div className={`${navContainerClass} ${className}`}>
      <div className={navContentClass}>
        {navItems.map(item => {
          const active = isActive(item.path, item.exact);
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`${styles.navButton} ${active ? styles.navButtonActive : ''}`}
              aria-label={item.label}
            >
              <div className={styles.navIconContainer}>{item.icon}</div>
              <span className={styles.navLabel}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MobileBottomNav;